/**
 * The rail filter's logic, kept free of Angular so every rule is a plain
 * function a spec can call.
 *
 * Two filters compose: a DOMAIN (one top-level nav group, or all of them) and a
 * free-text SEARCH. The domain keeps the rail's normal shape — sections,
 * accordion, children — and only drops the other sections. A search cannot:
 * the accordion only draws the children of the active branch, so a matching
 * sub-page under a closed parent would be filtered IN and still not appear. A
 * search therefore flattens its results into leaf rows, and a sub-page is
 * labelled with its parent ("Signage · Screens") so two areas that share a
 * name can still be told apart.
 *
 * Pinned groups (Overview) are not a domain. They stay put when a domain is
 * chosen, because home is never the reason someone narrowed the rail, and
 * they are searched like any other row.
 */
import type { PaperWorkspaceRailItem, PaperWorkspaceRailSection } from '@ricoh-paper/angular/shell';
import { navGroupSlug, type AdminNavGroup, type AdminNavItem } from '../../admin/config/admin-nav';

/** The domain value meaning "no domain filter". */
export const ALL_AREAS = 'all';

/** localStorage key for the chosen domain. Per browser profile, per user. */
export const NAV_DOMAIN_STORAGE_KEY = 'ws.admin-nav.domain';

export interface AdminNavFilter {
  /** Free text, as typed. */
  readonly query: string;
  /** A group name from the nav, or `ALL_AREAS`. */
  readonly domain: string;
}

/**
 * Fold text for matching: accents stripped, case folded, and anything that is
 * not a letter or digit treated as a word break, so "walk-in", "walk in" and
 * "Walk-In" all compare equal and "Café" matches "cafe".
 */
export function normaliseNavText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** The query split into folded words. Empty for a blank query. */
export function navQueryTokens(query: string): string[] {
  const folded = normaliseNavText(query);
  return folded ? folded.split(' ') : [];
}

/** True when the filter would change what the rail shows. */
export function isNavFilterActive(filter: AdminNavFilter): boolean {
  return filter.domain !== ALL_AREAS || navQueryTokens(filter.query).length > 0;
}

/** Every word must appear somewhere in the haystack (prefixes count). */
function matchesAll(haystack: string, tokens: readonly string[]): boolean {
  return tokens.every((t) => haystack.includes(t));
}

function haystack(parts: readonly (string | undefined)[]): string {
  return ` ${normaliseNavText(parts.filter(Boolean).join(' '))} `;
}

/** A nav item stripped to a leaf row, for search results. */
function asLeaf(item: AdminNavItem, label = item.label): AdminNavItem {
  const { children: _children, ...rest } = item;
  return { ...rest, label };
}

/** One group's search results, flattened. */
function searchGroup(group: AdminNavGroup, tokens: readonly string[]): AdminNavItem[] {
  const groupWords = [group.group, ...(group.keywords ?? [])];
  const out: AdminNavItem[] = [];
  for (const item of group.items) {
    const itemWords = [item.label, item.tags, ...(item.keywords ?? [])];
    if (item.route && matchesAll(haystack([...groupWords, ...itemWords]), tokens)) {
      out.push(asLeaf(item));
    }
    for (const child of item.children ?? []) {
      if (!child.route) continue;
      // A sub-page answers to its parent's name too: "policies" should find
      // every Policies page, not only the one literally called that.
      const words = [...groupWords, item.label, child.label, child.tags, ...(child.keywords ?? [])];
      if (matchesAll(haystack(words), tokens)) {
        out.push(asLeaf(child, `${item.label} · ${child.label}`));
      }
    }
  }
  return out;
}

/**
 * Apply a domain and a search to an already-gated nav. Pure: pass it
 * `visibleAdminNav(...)`, never the raw `ADMIN_NAV`, or the gating is lost.
 * Groups left with nothing are dropped, so an empty result is `[]`.
 */
export function filterAdminNav(
  groups: readonly AdminNavGroup[],
  filter: AdminNavFilter,
): readonly AdminNavGroup[] {
  const inDomain =
    filter.domain === ALL_AREAS
      ? groups
      : groups.filter((g) => g.hideHeading || g.group === filter.domain);
  const tokens = navQueryTokens(filter.query);
  if (!tokens.length) return inDomain;
  return inDomain
    .map((g) => ({ ...g, items: searchGroup(g, tokens) }))
    .filter((g) => g.items.length > 0);
}

/** The domains a person can choose from: the headed groups they can see. */
export function navDomains(groups: readonly AdminNavGroup[]): string[] {
  return groups.filter((g) => !g.hideHeading).map((g) => g.group);
}

/**
 * The domain to apply. A stored choice that names no visible group — renamed,
 * gated away, or not loaded yet — falls back to all areas without being
 * forgotten, so it applies again once the group appears.
 */
export function effectiveNavDomain(
  stored: string | null | undefined,
  domains: readonly string[],
): string {
  return stored && domains.includes(stored) ? stored : ALL_AREAS;
}

/** Every item and sub-page id in the nav. */
export function navItemIds(groups: readonly AdminNavGroup[]): Set<string> {
  const ids = new Set<string>();
  for (const g of groups) {
    for (const i of g.items) {
      ids.add(i.id);
      for (const c of i.children ?? []) ids.add(c.id);
    }
  }
  return ids;
}

/** Where an id sits: its label and its group. Null when it is not in the nav. */
export function findNavItem(
  groups: readonly AdminNavGroup[],
  id: string | null | undefined,
): { label: string; group: string } | null {
  if (!id) return null;
  for (const g of groups) {
    for (const i of g.items) {
      if (i.id === id) return { label: i.label, group: g.group };
      const child = i.children?.find((c) => c.id === id);
      if (child) return { label: child.label, group: g.group };
    }
  }
  return null;
}

// ── Persistence ─────────────────────────────────────────────────────────────

/** `localStorage`, or null where reading it throws (blocked site data, some
 *  private windows, sandboxed previews). */
export function navStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** The stored domain, or null. Never throws. */
export function readStoredNavDomain(storage: Pick<Storage, 'getItem'> | null): string | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(NAV_DOMAIN_STORAGE_KEY);
    return value && value !== ALL_AREAS ? value : null;
  } catch {
    return null;
  }
}

/** Store the domain; "all" clears the key. Never throws — a failed write only
 *  means the choice lasts for this page load. */
export function writeStoredNavDomain(
  storage: Pick<Storage, 'setItem' | 'removeItem'> | null,
  domain: string,
): boolean {
  if (!storage) return false;
  try {
    if (domain === ALL_AREAS) storage.removeItem(NAV_DOMAIN_STORAGE_KEY);
    else storage.setItem(NAV_DOMAIN_STORAGE_KEY, domain);
    return true;
  } catch {
    return false;
  }
}

// ── Rail model ──────────────────────────────────────────────────────────────

/** The key a parent row takes when it also owns a page of its own. */
export function railParentKey(id: string): string {
  return `${id}--section`;
}

/**
 * Adapt one migrated nav item to a Paper `PaperWorkspaceRailItem`. Relative
 * routes become absolute (`estate/locations` → `/estate/locations`).
 *
 * A PARENT THAT IS ALSO A PAGE. In `paper-workspace-rail` a row with children
 * discloses them and never navigates (the old admin rail's `parentsNavigate`
 * split control does not carry over). Spaces, Locations, Screens and Signage
 * are each a page AND the parent of their sub-pages, so the parent row takes
 * a key of its own (`railParentKey`) and the page itself becomes its first
 * child, under the same name and the item's own id. `matchActiveNav` keeps
 * answering with that id, so the child lights and the parent opens.
 */
function toRailItem(item: AdminNavItem): PaperWorkspaceRailItem {
  const route = item.route ? `/${item.route}` : undefined;
  const children = (item.children ?? []).map((c) => toRailItem(c));
  if (!children.length) {
    return {
      key: item.id,
      label: item.label,
      icon: item.icon,
      ...(route ? { route } : {}),
      ...(item.queryParams ? { queryParams: item.queryParams } : {}),
    };
  }
  if (!route) {
    return { key: item.id, label: item.label, icon: item.icon, children };
  }
  return {
    key: railParentKey(item.id),
    label: item.label,
    icon: item.icon,
    children: [
      {
        key: item.id,
        label: item.label,
        icon: item.icon,
        route,
        ...(item.queryParams ? { queryParams: item.queryParams } : {}),
      },
      ...children,
    ],
  };
}

/** Build the side nav from an already-filtered nav: General's items first
 *  with no heading (Overview), then one headed section per remaining group.
 *  Pure — pass it `visibleAdminNav(...)`, never the raw `ADMIN_NAV`, or the
 *  gating is lost again. Groups left empty are dropped. */
export function buildRailSections(groups: readonly AdminNavGroup[]): PaperWorkspaceRailSection[] {
  const pinned = groups.filter((g) => g.hideHeading).flatMap((g) => g.items.map((i) => toRailItem(i)));
  const sections = groups
    .filter((g) => !g.hideHeading && g.items.length > 0)
    .map((g) => ({
      id: navGroupSlug(g.group),
      label: g.group,
      items: g.items.map((i) => toRailItem(i)),
    }));
  return [...(pinned.length ? [{ id: 'pinned', items: pinned }] : []), ...sections];
}

/** Every routed row in the side nav, in reading order: what a search opens. */
export function railLeaves(sections: readonly PaperWorkspaceRailSection[]): PaperWorkspaceRailItem[] {
  return sections.flatMap((s) =>
    s.items.flatMap((i) => (i.children?.length ? i.children : [i])).filter((i) => !!i.route),
  );
}
